import { CloudSync, ContentCopy, Delete, Lock, LockOpen, Settings } from '@mui/icons-material';
import { ThemeProvider, useTheme } from '@mui/material';
import { useCallback, useMemo, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Html } from 'react-konva-utils';
import { useStudio } from '../../context';
import { StudioActionBarBox, StudioActionIconButton } from '../lib';

// react-konva-utils v0.2.0 does not export this type and we cannot upgrade until react 18
type TransformAttrs = Parameters<NonNullable<ComponentProps<typeof Html>['transformFunc']>>[0];

const ACTION_BAR_WIDTH = 130;
const ACTION_BAR_TEXT_WIDTH = 160;
const ACTION_BAR_OFFSET_TOP = 80;
const ACTION_BAR_OFFSET_BOTTOM = 30;
const TRANSFORM_RESET = { scaleX: 1, scaleY: 1, skewX: 1, skewY: 1 };

export const StudioActionBar = () => {
  // <Html> mounts a separate React root (ReactDOM.render), so context is lost again at that
  // boundary. Capture the theme here - the Layer-level bridge in index.tsx makes it the real
  // one - and re-provide it inside.
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    deleteItems,
    duplicateItems,
    selectedIds,
    selectedItems,
    setActiveSettingsTab,
    setDataItemCellSelectOpen,
    toggleLockItems,
    setSidebarItem,
    transformer: { dragging, transforming },
    stage,
  } = useStudio();

  const showDataSourceAction = useMemo(() => {
    if (selectedItems.length !== 1) return false;

    const item = selectedItems.at(0);
    if (!item) return false;

    return item.type === 'text' && !item.__dataItemId && !item.__dataItemKey;
  }, [selectedItems]);

  const showSettingsAction = useMemo(() => {
    if (selectedItems.length === 1) return true;
    return false;
  }, [selectedItems]);

  const hasLockedItem = useMemo(() => {
    return selectedItems.some((item) => item.locked);
  }, [selectedItems]);

  const actionBarWidth = useMemo(
    () => (showDataSourceAction ? ACTION_BAR_TEXT_WIDTH : ACTION_BAR_WIDTH),
    [showDataSourceAction],
  );

  // Calculate bounding box directly from selected items
  const itemsBox = useMemo(() => {
    if (!stage || selectedItems.length === 0) return undefined;

    const nodes = selectedItems
      .map((item) => stage.findOne(`#${item.id}`))
      .filter((node): node is NonNullable<typeof node> => node !== undefined);

    if (nodes.length === 0) return undefined;

    const rects = nodes.map((node) => node.getClientRect());
    const minX = Math.min(...rects.map((r) => r.x));
    const minY = Math.min(...rects.map((r) => r.y));
    const maxX = Math.max(...rects.map((r) => r.x + r.width));
    const maxY = Math.max(...rects.map((r) => r.y + r.height));

    return {
      x: minX / stage.scaleX(),
      y: minY / stage.scaleY(),
      width: (maxX - minX) / stage.scaleX(),
      height: (maxY - minY) / stage.scaleY(),
    };
  }, [selectedItems, stage]);

  const groupProps = useMemo(() => {
    if (!itemsBox || !stage) return { x: 0, y: 0 };

    // Use smaller offset when locked (no transformer handles to clear)
    const offsetTop = hasLockedItem ? 50 : ACTION_BAR_OFFSET_TOP;
    const topY = itemsBox.y - offsetTop / stage.scaleY();
    const bottomY = itemsBox.y + itemsBox.height + ACTION_BAR_OFFSET_BOTTOM;

    return {
      x: itemsBox.x + itemsBox.width / 2 - actionBarWidth / 2 / stage.scaleX(),
      y: topY > 0 ? topY : bottomY,
    };
  }, [actionBarWidth, hasLockedItem, stage, itemsBox]);

  const transformFunc = useCallback(
    (transforms: TransformAttrs) => ({ ...transforms, ...TRANSFORM_RESET }),
    [],
  );

  if (selectedItems.length === 0 || !itemsBox || dragging || transforming || !stage) return null;

  return (
    <Html groupProps={groupProps} transformFunc={transformFunc}>
      <ThemeProvider theme={theme}>
        <StudioActionBarBox sx={{ width: `${actionBarWidth}px` }}>
          {showSettingsAction && (
            <StudioActionIconButton
              disabled={hasLockedItem}
              label={t('pages.studio.edit.action_bar.settings')}
              onClick={(e) => {
                e.stopPropagation();
                setSidebarItem('Settings');
                setActiveSettingsTab('Settings');
              }}
              size="small"
            >
              <Settings />
            </StudioActionIconButton>
          )}
          {showDataSourceAction && (
            <StudioActionIconButton
              disabled={hasLockedItem}
              label={t('pages.studio.edit.action_bar.connect_data_source')}
              onClick={(e) => {
                e.stopPropagation();
                setSidebarItem('Settings');
                setActiveSettingsTab('Data Source');
                setDataItemCellSelectOpen(true);
              }}
            >
              <CloudSync />
            </StudioActionIconButton>
          )}
          <StudioActionIconButton
            label={
              hasLockedItem
                ? t('pages.studio.edit.action_bar.unlock')
                : t('pages.studio.edit.action_bar.lock')
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleLockItems(selectedItems.map((x) => x.id));
            }}
            size="small"
          >
            {hasLockedItem ? <Lock /> : <LockOpen />}
          </StudioActionIconButton>
          <StudioActionIconButton
            disabled={hasLockedItem}
            label={t('pages.studio.edit.action_bar.duplicate')}
            onClick={(e) => {
              e.stopPropagation();
              duplicateItems(selectedItems);
            }}
            size="small"
          >
            <ContentCopy />
          </StudioActionIconButton>
          <StudioActionIconButton
            disabled={hasLockedItem}
            label={t('pages.studio.edit.action_bar.delete')}
            onClick={(e) => {
              e.stopPropagation();
              deleteItems([...selectedIds]);
            }}
            size="small"
            sx={{ '& svg': { color: hasLockedItem ? undefined : theme.palette.error.main } }}
          >
            <Delete />
          </StudioActionIconButton>
        </StudioActionBarBox>
      </ThemeProvider>
    </Html>
  );
};
